import { useParams, Link } from 'react-router-dom'
import { ArrowLeft, Compass, Music2 } from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { AvatarLeaderboard } from '@/components/dashboard/AvatarLeaderboard'
import { SongLeaderboard } from '@/components/instructor/SongLeaderboard'
import { EssentialsGapList } from '@/components/instructor/EssentialsGapList'
import { useInstructorThemeStats } from '@/hooks/useInstructorThemeStats'
import { formatPrecise } from '@/lib/format'
import { workoutNouns } from '@/lib/discipline'

// A theme like "80s" can span an instructor's classes across more than
// one discipline, so "workouts" rather than "rides".
const { plural: WORKOUTS } = workoutNouns('all')

export function ThemeDeepDivePage() {
  const { id, theme } = useParams<{ id: string; theme: string }>()
  const { data, error } = useInstructorThemeStats(id, theme)

  return (
    <div className="mx-auto flex max-w-6xl flex-col gap-8 px-6 py-10">
      <Link
        to={`/instructors/${id}`}
        className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="size-4" />
        Back to {data?.instructor.name ?? 'instructor'}
      </Link>

      {error && (
        <Card className="border-destructive/50">
          <CardContent className="py-4 text-sm text-destructive">Couldn&apos;t load this theme: {error}</CardContent>
        </Card>
      )}

      {data ? (
        <header>
          <h1 className="text-2xl font-semibold tracking-tight">
            {data.instructor.name} &middot; {data.theme}
          </h1>
          <p className="text-sm text-muted-foreground">
            {formatPrecise(data.ride_count)} {WORKOUTS} tagged &ldquo;{data.theme}&rdquo; so far
          </p>
        </header>
      ) : (
        <div className="flex flex-col gap-2">
          <Skeleton className="h-8 w-80" />
          <Skeleton className="h-4 w-48" />
        </div>
      )}

      <section className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Music2 className="size-4" />
              Go-to songs
            </CardTitle>
            <CardDescription>Ranked by how many {theme} {WORKOUTS} feature them</CardDescription>
          </CardHeader>
          <CardContent>
            {data ? (
              <SongLeaderboard songs={data.top_songs} />
            ) : (
              <div className="flex flex-col gap-3">
                {Array.from({ length: 8 }).map((_, i) => (
                  <Skeleton key={i} className="h-12 w-full" />
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Go-to artists</CardTitle>
            <CardDescription>Same {WORKOUTS}, rolled up by artist</CardDescription>
          </CardHeader>
          <CardContent>
            {data ? (
              <AvatarLeaderboard
                entries={data.top_artists.map((a) => ({
                  id: a.id,
                  name: a.name,
                  image_url: a.image_url,
                  count: a.play_count,
                }))}
                valueLabel={WORKOUTS}
              />
            ) : (
              <div className="flex flex-col gap-3">
                {Array.from({ length: 8 }).map((_, i) => (
                  <Skeleton key={i} className="h-10 w-full" />
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </section>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Compass className="size-4" />
            Essentials still missing
          </CardTitle>
          <CardDescription>
            {data?.theme ?? theme} tracks the wider Last.fm community loves that haven&apos;t shown up in the rotation
            yet
          </CardDescription>
        </CardHeader>
        <CardContent>
          {data ? (
            data.missing_essentials.length > 0 ? (
              <EssentialsGapList essentials={data.missing_essentials} />
            ) : (
              <p className="text-sm text-muted-foreground">
                No canonical gap data available for &ldquo;{data.theme}&rdquo; yet.
              </p>
            )
          ) : (
            <div className="flex flex-col gap-3">
              {Array.from({ length: 6 }).map((_, i) => (
                <Skeleton key={i} className="h-10 w-full" />
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
